import { useCallback, useEffect, useRef, useState } from 'react';
import {
  adminRequest,
  AdminApiError,
  decideQuote,
  type AdminReviewFilters,
  type PendingReviewerOption,
  type ReviewDecision,
} from './admin-api';
import { ReviewFilters } from './app';
import { UnifiedModerationCard } from './quote-moderation-card';
import {
  quoteModerationCopy as c,
  control,
  initialFilters,
  moderationKey,
  reviewKey,
  parseQuoteModerationQueue,
  type QuoteModerationItem,
  type Queue,
  type PairDecision,
} from './quote-moderation-model';
export {
  quoteModerationCopy,
  moderationKey,
  parseQuoteModerationQueue,
  type QuoteModerationItem,
} from './quote-moderation-model';
export { UnifiedModerationCard } from './quote-moderation-card';
export function UnifiedQuoteQueue({
  getAccessToken,
  onAccessDenied,
}: {
  getAccessToken(): Promise<string>;
  onAccessDenied(): void;
}) {
  const [skipped, setSkipped] = useState<string[]>([]);
  const heading = useRef<HTMLHeadingElement>(null);
  const [readers, setReaders] = useState<PendingReviewerOption[]>([]);
  const [queue, setQueue] = useState<Queue | null>(null),
    [filters, setFilters] = useState(initialFilters);
  const [kind, setKind] = useState('all'),
    [status, setStatus] = useState('pending'),
    [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false),
    [decisionBusy, setDecisionBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null),
    [notice, setNotice] = useState<string | null>(null);
  const generation = useRef(0),
    inFlight = useRef(false);
  const load = useCallback(
    async (cursor?: string) => {
      const run = ++generation.current;
      setBusy(true);
      setError(null);
      if (!cursor) setQueue(null);
      try {
        const query = new URLSearchParams({
          kind,
          status,
          search,
          readerIds: filters.reviewerIds.join(','),
        });
        if (filters.checkStatus) query.set('checkStatus', filters.checkStatus);
        if (cursor) query.set('cursor', cursor);
        const next = parseQuoteModerationQueue(
          await adminRequest(getAccessToken, `/admin/quote-moderation/pending?${query}`),
        );
        if (run === generation.current) setReaders(next.readers);
        if (run === generation.current)
          setQueue((current) => ({
            ...next,
            items:
              cursor && current
                ? [
                    ...current.items,
                    ...next.items.filter(
                      (item) =>
                        !current.items.some((old) => moderationKey(old) === moderationKey(item)),
                    ),
                  ]
                : next.items,
          }));
      } catch (cause) {
        if (run === generation.current) {
          if (cause instanceof AdminApiError && cause.status === 403) onAccessDenied();
          else setError(c.loadError);
        }
      } finally {
        if (run === generation.current) setBusy(false);
      }
    },
    [getAccessToken, onAccessDenied, kind, status, search, filters],
  );
  useEffect(() => {
    void load();
    return () => {
      generation.current++;
    };
  }, [load]);
  async function decide(
    item: QuoteModerationItem,
    decision: ReviewDecision,
    message?: string | null,
    spoilers = false,
    pairDecision?: PairDecision,
  ) {
    if (inFlight.current || busy) return;
    inFlight.current = true;
    setDecisionBusy(moderationKey(item));
    setError(null);
    setNotice(null);
    try {
      if (item.kind === 'thought' && item.quote) {
        await adminRequest(getAccessToken, `/admin/quote-moderation/thoughts/${item.id}/decision`, {
          method: 'POST',
          body: JSON.stringify({
            expectedThoughtVersion: item.version,
            expectedQuoteVersion: item.quote.version,
            expectedCaptureVersion: item.quote.captureVersion,
            quoteId: item.quote.id,
            decision:
              pairDecision ?? (decision === 'approved' ? 'approve_both' : 'decline_thought'),
            containsSpoilers: spoilers,
          }),
        });
      } else if (item.kind === 'shared') {
        if (!item.readerId) throw new Error('Missing shared quote reader');
        await decideQuote(getAccessToken, item, decision, message ?? null);
      } else
        await adminRequest(getAccessToken, `/admin/lines/${item.kind}/${item.id}/decision`, {
          method: 'POST',
          body: JSON.stringify({
            expectedVersion: item.version,
            decision: decision === 'rejected' && item.kind === 'wording' ? 'hidden' : decision,
            ...(item.kind === 'thought' ? { containsSpoilers: spoilers } : {}),
          }),
        });
      setNotice(
        pairDecision === 'quote_only'
          ? c.quoteOnlyNotice
          : decision === 'approved'
            ? c.approvedNotice
            : item.kind === 'wording'
              ? c.hiddenNotice
              : c.declinedNotice,
      );
      await load();
      heading.current?.focus();
    } catch (cause) {
      if (cause instanceof AdminApiError && cause.status === 403) onAccessDenied();
      else
        setError(
          cause instanceof AdminApiError && cause.status === 409 ? c.conflict : c.decisionError,
        );
    } finally {
      inFlight.current = false;
      setDecisionBusy(null);
    }
  }
  const available =
    queue?.items.filter((item) => status !== 'pending' || !skipped.includes(reviewKey(item))) ?? [];
  function skip(item: QuoteModerationItem) {
    setSkipped((current) => [...new Set([...current, reviewKey(item)])]);
    setNotice(c.skippedNotice);
    heading.current?.focus();
    if (available.length === 1 && queue?.nextCursor) void load(queue.nextCursor);
  }
  return (
    <section>
      <div className="admin-page-header flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="admin-eyebrow">{c.moderation}</p>
          <h1 ref={heading} tabIndex={-1} className="m-0 font-serif font-normal">
            {c.nav}
          </h1>
          <p className="mb-0 mt-3 text-sm text-ink-soft">{c.intro}</p>
        </div>
        <div className="flex items-center gap-4">
          <span aria-live="polite" className="text-sm tabular-nums text-ink-muted">
            {queue ? `${queue.total} ${status === 'pending' ? c.waiting : c.matching}` : c.loading}
          </span>
          <button
            className={control}
            disabled={busy || decisionBusy !== null}
            onClick={() => void load()}
          >
            {c.refresh}
          </button>
        </div>
      </div>
      {(kind !== 'all' ||
        status !== 'pending' ||
        search ||
        filters.checkStatus ||
        filters.reviewerIds.length > 0) && (
        <button
          className={`${control} mb-4`}
          disabled={decisionBusy !== null}
          onClick={() => {
            setKind('all');
            setStatus('pending');
            setSearch('');
            setFilters(initialFilters);
          }}
        >
          {c.clear}
        </button>
      )}
      {skipped.length > 0 && (
        <button
          className={`${control} mb-4 ml-2`}
          disabled={busy || decisionBusy !== null}
          onClick={() => {
            setSkipped([]);
            setNotice(null);
            void load();
          }}
        >
          {c.returnSkipped} ({skipped.length})
        </button>
      )}
      <fieldset disabled={decisionBusy !== null} className="m-0 min-w-0 border-0 p-0">
        <div className="mb-5 flex flex-wrap gap-4">
          <label className="grid gap-2 text-xs text-ink-muted">
            {c.content}
            <select
              className="admin-input"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value);
                if (e.target.value !== 'wording') setStatus('pending');
              }}
            >
              <option value="all">{c.all}</option>
              <option value="shared">{c.sharedFilter}</option>
              <option value="wording">{c.wordingFilter}</option>
              <option value="thought">{c.thoughtFilter}</option>
            </select>
          </label>
          <label className="grid gap-2 text-xs text-ink-muted">
            {c.status}
            <select
              className="admin-input"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                if (e.target.value !== 'pending') setKind('wording');
              }}
            >
              <option value="pending">{c.pending}</option>
              <option value="approved">{c.approvedFilter}</option>
              <option value="hidden">{c.hiddenFilter}</option>
            </select>
          </label>
          <label className="grid min-w-48 flex-1 gap-2 text-xs text-ink-muted">
            {c.search}
            <input
              className="admin-input"
              type="search"
              maxLength={200}
              placeholder={c.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>
        <ReviewFilters
          contentType={c.quoteLabel}
          selectedFilters={filters}
          reviewerOptions={readers}
          onChange={setFilters}
        />
      </fieldset>
      {filters.reviewerIds.length > 0 && <p className="text-sm text-ink-muted">{c.readerHint}</p>}
      {error && (
        <p role="alert" className="mb-5 text-warning">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mb-5 text-safe">
          {notice}
        </p>
      )}
      {busy && (
        <p role="status" className="text-sm text-ink-muted">
          {c.loadingItems}
        </p>
      )}
      {queue?.items.length === 0 && !busy && (
        <div className="admin-empty">
          <h2 className="font-serif text-2xl">
            {kind === 'all' && !search && !filters.checkStatus && !filters.reviewerIds.length
              ? c.empty
              : c.noMatch}
          </h2>
          <p className="text-sm text-ink-soft">
            {kind === 'all' &&
            status === 'pending' &&
            !search &&
            !filters.checkStatus &&
            !filters.reviewerIds.length
              ? c.emptyHint
              : c.filterHint}
          </p>
        </div>
      )}
      {status === 'pending' &&
        queue &&
        queue.items.length > 0 &&
        available.length === 0 &&
        !busy && (
          <p role="status" className="my-6 text-ink-soft">
            {c.allSkipped}
          </p>
        )}
      <div
        aria-busy={busy}
        className={
          status === 'pending'
            ? 'grid grid-cols-1 items-start gap-5 lg:grid-cols-2 lg:gap-6'
            : 'columns-1 gap-5 lg:columns-2 lg:gap-6'
        }
      >
        {(status === 'pending' ? available.slice(0, 2) : available).map((item) => (
          <UnifiedModerationCard
            key={reviewKey(item)}
            item={item}
            busy={decisionBusy !== null || busy}
            onSkip={status === 'pending' ? () => skip(item) : undefined}
            onPairDecision={(decision, spoilers) =>
              void decide(
                item,
                decision === 'approve_both' ? 'approved' : 'rejected',
                null,
                spoilers,
                decision,
              )
            }
            onDecision={(decision, message, spoilers) =>
              void decide(item, decision, message, spoilers)
            }
          />
        ))}
      </div>
      {queue?.nextCursor && (
        <button
          disabled={busy || decisionBusy !== null}
          className={`${control} mt-5`}
          onClick={() => void load(queue.nextCursor!)}
        >
          {c.more} · {queue.items.length} {c.of} {queue.total}
        </button>
      )}
    </section>
  );
}
